import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Box,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
} from "recharts";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";

const COLORS = ["#1976d2", "#9c27b0", "#ff9800", "#4caf50", "#f44336"];

const StatsDashboard = ({ data = {} }) => {
  // default fallbacks
  const monthly = data.monthlyCustomers || [];
  const topCustomers = data.topCustomers || [];
  const customersBySource = data.customersBySource || [];

  // compute growth %
  const thisMonth = monthly[monthly.length - 1]?.count || 0;
  const lastMonth = monthly[monthly.length - 2]?.count || 0;
  const growth =
    lastMonth > 0 ? (((thisMonth - lastMonth) / lastMonth) * 100).toFixed(1) : 0;
  const isPositive = growth >= 0;

  return (
    <Grid container spacing={3}>
      {/* Total Customers */}
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Total Customers
            </Typography>
            <Typography variant="h4" color="primary">
              {data.totalCustomers ?? 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Active Customers */}
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Active Customers
            </Typography>
            <Typography variant="h4" color="secondary">
              {data.activeCustomers ?? 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* This Month Customers + Growth */}
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              This Month Customers
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h4">{thisMonth}</Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: isPositive ? "success.main" : "error.main",
                  fontWeight: "bold",
                }}
              >
                {isPositive ? (
                  <ArrowUpward fontSize="small" />
                ) : (
                  <ArrowDownward fontSize="small" />
                )}
                {growth}%
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Placeholder for balance - could add another metric card */}
      {/* <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Customer Satisfaction
            </Typography>
            <Typography variant="h4" color="primary">
              {data.satisfactionRate ? `${data.satisfactionRate}%` : "N/A"}
            </Typography>
          </CardContent>
        </Card>
      </Grid> */}

      {/* Customers by Source (Pie Chart) */}
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom align="center">
              Customers by Source
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={customersBySource}
                  dataKey="count"
                  nameKey="_id"
                  outerRadius={100}
                  label
                >
                  {customersBySource.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Monthly Trend (Bar Chart) */}
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom align="center">
              Customer Growth by Month
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthly}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#1976d2" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Top Customers */}
      <Grid item xs={12}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Top Customers
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              {topCustomers.map((customer) => (
                <ListItem key={customer._id} divider>
                  <ListItemText
                    primary={customer.name}
                    secondary={`Bookings: ${customer.totalBookings} | Spent: $${customer.totalSpent}`}
                  />
                </ListItem>
              ))}
              {topCustomers.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No top customers available
                </Typography>
              )}
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default StatsDashboard;